import {
  diffQuestContent,
  isDataClientError,
  type QuestGateRefusalDetails,
  type QuestMissionContent,
} from '@kk/domain';
import { Card, SectionLabel, Text, Title2 } from '@kk/ui';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useContentCan, useSuperAdmin } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import {
  useCopyVersionToDraft,
  useErrorMessage,
  useMissionVersions,
  useQuestAudit,
  useQuestContent,
  useRestoreVersion,
} from '@/data/hooks';
import { relativeTime } from '@/lib/time';
import type { AdminMission, QuestMissionVersion } from '@/types/wire';

const c = deck.o13;

/** The draft as the diff reads it: the working copy O06 edits, in a version's shape. */
function draftContent(m: AdminMission): QuestMissionContent {
  return {
    title: m.title,
    ...(m.levelName ? { levelName: m.levelName } : {}),
    ...(m.mechanic ? { mechanic: m.mechanic } : {}),
    deepLink: m.deepLink ?? null,
    ...(m.story ? { story: m.story } : {}),
    ...(m.beats ? { beats: m.beats as QuestMissionContent['beats'] } : {}),
    quiz: m.quiz as QuestMissionContent['quiz'],
    ...(m.badge ? { badge: m.badge } : {}),
    ...(m.art ? { art: m.art } : {}),
    ...(m.scripts.reader?.text || m.scripts.prereader?.text
      ? {
          scripts: {
            ...(m.scripts.reader?.text ? { reader: m.scripts.reader.text } : {}),
            ...(m.scripts.prereader?.text ? { prereader: m.scripts.prereader.text } : {}),
          },
        }
      : {}),
  };
}

function VersionCard({
  v,
  live,
  picked,
  onPick,
  onRestore,
  onCopy,
  restoring,
}: {
  v: QuestMissionVersion;
  live: boolean;
  picked: boolean;
  onPick: () => void;
  onRestore: () => void;
  onCopy: () => void;
  restoring: boolean;
}) {
  const reader = v.reviews.reader?.by;
  const prereader = v.reviews.prereader?.by;
  // the six roles: restoring is publishing; copying to the draft is editing
  const mayPublish = useContentCan('publish');
  const mayEdit = useContentCan('edit');
  return (
    <div className={`version-card${live ? ' live' : ''}`} data-testid={`version-${v.version}`}>
      <div className="stack">
        <Text variant="labelStrong">{c.version(v.version, live)}</Text>
        <Text variant="bodySm" tone="muted">
          {c.publishedBy(v.publishedBy, relativeTime(v.publishedAt))}
        </Text>
        {reader || prereader ? (
          <Text variant="bodySm" tone="muted">
            {c.reviewedBy(reader, prereader)}
          </Text>
        ) : v.note ? null : (
          <Text variant="bodySm" tone="muted">
            {c.reviewedBefore}
          </Text>
        )}
        {v.note ? (
          <Text variant="bodySm" tone="muted">
            {v.note}
          </Text>
        ) : null}
      </div>
      <div className="actions">
        <button
          type="button"
          className={`chip${picked ? ' on' : ''}`}
          aria-pressed={picked}
          onClick={onPick}
          data-testid={`compare-${v.version}`}
        >
          {picked ? c.comparing : c.compare}
        </button>
        {/* restoring the live version would change nothing, so it is not offered (§4: restore is a publish) */}
        {live ? null : (
          <>
            {mayPublish ? (
              <LinkButton
                label={c.restore}
                onPress={onRestore}
                disabled={restoring}
                testID={`restore-${v.version}`}
              />
            ) : null}
            {/* the way back for a version today's gate refuses: fix it in the draft, review, publish */}
            {mayEdit ? <LinkButton label={c.copyToDraft} onPress={onCopy} testID={`copy-${v.version}`} /> : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * **O13 · a mission's version history** (CONTENT_ENGINE.md §4 and §8: "a mission's versions, a diff between any two, and
 * restore"). `/quest/:adventureId/:missionId/history` on the admin host (§8.1).
 */
export function MissionHistoryScreen() {
  const superAdmin = useSuperAdmin();
  const errorMessage = useErrorMessage();
  const navigate = useNavigate();
  const { adventureId, missionId } = useParams<{ adventureId: string; missionId: string }>();
  const content = useQuestContent(superAdmin);
  const history = useMissionVersions(superAdmin ? (missionId ?? null) : null);
  const audit = useQuestAudit(superAdmin ? (missionId ?? null) : null);
  const restore = useRestoreVersion();
  const copy = useCopyVersionToDraft();
  const [picked, setPicked] = useState<number[]>([]);
  const [flash, setFlash] = useState<string | null>(null);

  if (!superAdmin) return <div data-testid="admin-only">{deck.o06.adminOnly}</div>;
  const mission = (content.data ?? []).flatMap((a) => a.missions).find((m) => m.id === missionId);
  const h = history.data;
  const versions = h?.versions ?? [];

  const pick = (n: number) =>
    setPicked((now) =>
      now.includes(n) ? now.filter((x) => x !== n) : [...now, n].slice(-2).sort((a, b) => a - b),
    );
  const byNumber = (n: number) => versions.find((v) => v.version === n);
  const pair: [number, number | 'draft'] | null =
    picked.length === 2
      ? [picked[0]!, picked[1]!]
      : picked.length === 1 && h?.hasDraft && mission
        ? [picked[0]!, 'draft']
        : null;
  const diff = pair
    ? diffQuestContent(
        byNumber(pair[0])!.content as QuestMissionContent,
        pair[1] === 'draft' ? draftContent(mission!) : (byNumber(pair[1])!.content as QuestMissionContent),
      )
    : null;

  return (
    <div className="stack" data-testid="mission-history">
      <LinkButton
        label={c.back}
        onPress={() => navigate(`/quest/${adventureId}/${missionId}`)}
        testID="history-back"
      />
      <Title2>{mission ? deck.o06.missionEyebrow(mission.order, mission.title) : ''}</Title2>
      <SectionLabel>{c.history}</SectionLabel>
      {history.error ? <Status>{errorMessage(history.error)}</Status> : null}
      {flash ? <Status testID="history-flash">{flash}</Status> : null}
      {pair ? null : (
        <Text variant="bodySm" tone="muted" testID="compare-hint">
          {h?.hasDraft ? c.hintWithDraft : c.hint}
        </Text>
      )}
      <div className="stack">
        {versions.map((v) => (
          <VersionCard
            key={v.version}
            v={v}
            live={v.version === h?.liveVersion}
            picked={picked.includes(v.version)}
            onPick={() => pick(v.version)}
            restoring={restore.isPending}
            onRestore={() =>
              restore.mutate(
                { id: missionId!, version: v.version },
                {
                  onSuccess: (next) => {
                    setPicked([]);
                    setFlash(c.restored(v.version, next.liveVersion ?? v.version));
                  },
                  // restore is a publish: a version today's checker refuses says so, and points at the draft
                  onError: (e) => {
                    const details = isDataClientError(e) ? (e.details as QuestGateRefusalDetails | undefined) : undefined;
                    setFlash(
                      details?.reason === 'checker'
                        ? c.restoreFails(v.version, details.issues.length)
                        : errorMessage(e),
                    );
                  },
                },
              )
            }
            onCopy={() =>
              copy.mutate(
                { id: missionId!, version: v.version },
                {
                  onSuccess: () => setFlash(c.copied(v.version)),
                  onError: (e) => setFlash(errorMessage(e)),
                },
              )
            }
          />
        ))}
      </div>

      {pair && diff ? (
        <div data-testid="compare">
        <Card>
          <SectionLabel>{c.compareTitle(pair[0], pair[1])}</SectionLabel>
          {diff.length === 0 ? <Text variant="bodySm">{c.noDifferences}</Text> : null}
          {diff.map((row) => (
            <div className="diff-row" key={row.where} data-testid="diff-row">
              <Text variant="labelStrong">{row.where}</Text>
              <div className="diff-before">
                {row.before.map((t, i) => (
                  <span key={i} className={`diff-${t.kind}`}>
                    {t.text}
                  </span>
                ))}
              </div>
              <div className="diff-after">
                {row.after.map((t, i) => (
                  <span key={i} className={`diff-${t.kind}`}>
                    {t.text}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Card>
        </div>
      ) : null}
      {(audit.data ?? []).length > 0 ? (
        <div data-testid="audit">
        <Card>
          <SectionLabel>{c.audit}</SectionLabel>
          {audit.data!.map((a) => (
            <div className="audit-row" key={a.id}>
              <Text variant="bodySm" tone="muted">
                {relativeTime(a.at)}
              </Text>
              <Text variant="labelStrong">{a.by}</Text>
              <Text variant="bodySm">{a.what}</Text>
            </div>
          ))}
        </Card>
        </div>
      ) : null}
    </div>
  );
}
